import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

/**
 * Reordena o grid de produtos: disponíveis primeiro, esgotados por último.
 * Usa um flag para evitar loop infinito com o MutationObserver.
 *
 * @param {HTMLElement} grid - O elemento <ul> do grid de produtos
 */
function sortProductsByAvailability(grid) {
  if (!grid) return;

  const items = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
  if (items.length === 0) return;

  const available = [];
  const unavailable = [];

  for (const item of items) {
    if (item.dataset.available === 'true') {
      available.push(item);
    } else {
      unavailable.push(item);
    }
  }

  // Sem embaralhamento: mantém a ordem original do Shopify dentro de cada grupo
  const sorted = [...available, ...unavailable];

  // Verifica se a ordem já está correta para não fazer DOM ops desnecessárias
  let alreadySorted = true;
  for (let i = 0; i < items.length; i++) {
    if (items[i] !== sorted[i]) {
      alreadySorted = false;
      break;
    }
  }

  if (alreadySorted) return;

  // Usa DocumentFragment para reordenar em uma única operação DOM
  const fragment = document.createDocumentFragment();
  for (const item of sorted) {
    fragment.appendChild(item);
  }
  grid.appendChild(fragment);
}

// Executa na carga inicial
document.addEventListener('DOMContentLoaded', () => {
  const applySort = () => {
    const grid = document.querySelector('.product-grid');
    if (grid) sortProductsByAvailability(grid);
  };

  applySort();

  // Re-aplica após recargas via filtros/seção
  document.addEventListener('shopify:section:load', applySort);
  document.addEventListener('collection:reloaded', applySort);

  // Observa apenas adição de novos cards via infinite scroll (não subtree inteiro)
  // Usa flag para quebrar o ciclo: ao reordenar os itens já presentes, o observer
  // detectaria a movimentação e chamaria sort novamente → loop.
  let isSorting = false;
  const gridContainer = document.querySelector('#ResultsList');

  if (gridContainer) {
    const observer = new MutationObserver((mutations) => {
      // Ignora callbacks disparados pela própria reordenação
      if (isSorting) return;

      // Só reage se houver nós adicionados (novos cards via infinite scroll)
      const hasAddedNodes = mutations.some((m) => m.addedNodes.length > 0);
      if (!hasAddedNodes) return;

      const grid = gridContainer.querySelector('.product-grid');
      if (!grid) return;

      isSorting = true;
      sortProductsByAvailability(grid);
      isSorting = false;
    });

    observer.observe(gridContainer, { childList: true, subtree: true });
  }
});
